import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

export default function TopupBonuses() {
  const { t } = useLang();
  const { labId, currentLab } = useLab();
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ min_amount: '', bonus_amount: '' });
  const [editingId, setEditingId] = useState(null);
  const [bonusPercent, setBonusPercent] = useState('');
  const [savingPercent, setSavingPercent] = useState(false);

  const load = useCallback(() => {
    if (!labId) return;
    setLoading(true);
    Promise.all([
      api.get(`/owner/topup-bonuses?lab_id=${labId}`),
      api.get('/owner/labs'),
    ])
      .then(([r, labs]) => {
        setBonuses(r.data || []);
        const lab = (labs.data || []).find(l => l.id === labId || l.id === parseInt(labId));
        setBonusPercent(String(parseFloat(lab?.topup_bonus_percent || 0) || ''));
      })
      .catch(() => setBonuses([]))
      .finally(() => setLoading(false));
  }, [labId]);

  useEffect(() => { load(); }, [load]);

  const savePercent = async () => {
    const pct = parseFloat(bonusPercent);
    if (isNaN(pct) || pct < 0 || pct > 100) {
      alert('Foiz 0–100 oralig\'ida bo\'lishi kerak');
      return;
    }
    setSavingPercent(true);
    try {
      await api.patch(`/owner/labs/${labId}/settings`, { topup_bonus_percent: pct });
      load();
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally {
      setSavingPercent(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const minRaw = (form.min_amount || '').toString().replace(/\s|,/g, '');
    const bonRaw = (form.bonus_amount || '').toString().replace(/\s|,/g, '');
    const min = parseFloat(minRaw);
    const bon = parseFloat(bonRaw);
    if (!labId) {
      alert(t('topupNoLabAlert'));
      return;
    }
    if (!(min > 0)) {
      alert(`${t('topupInvalidMin')}: "${form.min_amount}"`);
      return;
    }
    if (!(bon > 0)) {
      alert(`${t('topupInvalidBonus')}: "${form.bonus_amount}"`);
      return;
    }
    try {
      if (editingId) {
        await api.put(`/owner/topup-bonuses/${editingId}`, { min_amount: min, bonus_amount: bon });
      } else {
        await api.post('/owner/topup-bonuses', { lab_id: labId, min_amount: min, bonus_amount: bon });
      }
      setForm({ min_amount: '', bonus_amount: '' });
      setEditingId(null);
      load();
    } catch (e) {
      alert(t('error') + ': ' + (e.response?.data?.error || e.message));
    }
  };

  const edit = (b) => {
    setEditingId(b.id);
    setForm({ min_amount: String(b.min_amount), bonus_amount: String(b.bonus_amount) });
  };

  const remove = async (b) => {
    if (!window.confirm(`${Number(b.min_amount).toLocaleString()} ${t('currency')} ${t('topupConfirmDelete')}`)) return;
    try {
      await api.delete(`/owner/topup-bonuses/${b.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.put(`/owner/topup-bonuses/${b.id}`, { is_active: !b.is_active });
      load();
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>{t('topupTitle')}</h1>
        <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>
          {t('topupCurrentBranch')} <b style={{ color: 'var(--cyan)' }}>{currentLab?.name || '—'}</b>.{' '}
          {t('topupDesc')}
        </div>
      </div>

      {/* FOIZLI BONUS BLOKI */}
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)',
        borderRadius: 14, padding: 18, marginBottom: 22,
      }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
          🎁 Foizli bonus (barcha summalar uchun)
        </div>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
          Gamer qancha to'ldirsa ham, shu foizda bonus qo'shiladi.
          Masalan 30% → 100 000 so'm tashasa 130 000 tushadi.
          {parseFloat(bonusPercent) > 0 && (
            <span style={{ color: 'var(--green)', marginLeft: 8 }}>
              Hozir: <b>+{bonusPercent}%</b> aktiv
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="number" min="0" max="100" step="1"
              placeholder="0"
              value={bonusPercent}
              onChange={e => setBonusPercent(e.target.value)}
              style={{
                width: 100, background: 'var(--bg2)', border: '1px solid var(--border)',
                color: 'var(--text)', padding: '9px 32px 9px 12px',
                borderRadius: 8, fontSize: 14, fontWeight: 700,
              }}
            />
            <span style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              color: 'var(--text3)', fontSize: 13,
            }}>%</span>
          </div>
          <button
            onClick={savePercent}
            disabled={savingPercent}
            style={{
              background: 'linear-gradient(135deg, var(--green), #059669)',
              border: 'none', color: '#fff',
              padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              opacity: savingPercent ? 0.6 : 1,
            }}
          >
            {savingPercent ? 'Saqlanmoqda...' : '💾 Saqlash'}
          </button>
          {parseFloat(bonusPercent) > 0 && (
            <button
              onClick={() => { setBonusPercent('0'); }}
              style={{
                background: 'transparent', border: '1px solid var(--red)',
                color: 'var(--red)', padding: '10px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
              }}
            >
              O'chirish
            </button>
          )}
        </div>
        {parseFloat(bonusPercent) > 0 && (
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text3)' }}>
            Misol: 50 000 → <b style={{ color: 'var(--green)' }}>+{Math.round(50000 * parseFloat(bonusPercent) / 100).toLocaleString()} bonus</b> ·
            100 000 → <b style={{ color: 'var(--green)' }}> +{Math.round(100000 * parseFloat(bonusPercent) / 100).toLocaleString()} bonus</b> ·
            200 000 → <b style={{ color: 'var(--green)' }}> +{Math.round(200000 * parseFloat(bonusPercent) / 100).toLocaleString()} bonus</b>
          </div>
        )}
      </div>

      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
        Yoki <b>bosqichli bonus</b> belgilang (foiz 0 bo'lganda ishlaydi):
      </div>

      <div style={{
        background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.25)',
        borderRadius: 12, padding: 14, marginBottom: 22, fontSize: 12, color: 'var(--text2)',
      }}>
        💡 <b>10 000 → +500 · 50 000 → +5 000 · 100 000 → +15 000</b>
      </div>

      <form onSubmit={submit} style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14,
        padding: 18, marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 5 }}>{t('topupMinAmountLabel')}</label>
          <input
            type="text" inputMode="numeric" placeholder="50000"
            value={form.min_amount}
            onChange={e => setForm(f => ({ ...f, min_amount: e.target.value.replace(/[^\d]/g, '') }))}
            style={{ width: 160, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13 }}
          />
        </div>
        <div style={{ color: 'var(--text3)', paddingBottom: 11 }}>→</div>
        <div>
          <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 5 }}>{t('topupBonusAmountLabel')}</label>
          <input
            type="text" inputMode="numeric" placeholder="20000"
            value={form.bonus_amount}
            onChange={e => setForm(f => ({ ...f, bonus_amount: e.target.value.replace(/[^\d]/g, '') }))}
            style={{ width: 160, background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13 }}
          />
        </div>
        <button type="submit" style={{
          background: 'linear-gradient(135deg, var(--purple), #5b21b6)', border: 'none',
          color: '#fff', padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>{editingId ? `💾 ${t('save')}` : t('topupAddRule')}</button>
        {editingId && (
          <button type="button" onClick={() => { setEditingId(null); setForm({ min_amount: '', bonus_amount: '' }); }}
            style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: '10px 16px', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}>
            {t('cancelShort')}
          </button>
        )}
      </form>

      {loading ? (
        <div style={{ color: 'var(--text3)', textAlign: 'center', padding: 40 }}>{t('loading')}</div>
      ) : bonuses.length === 0 ? (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 10 }}>🎁</div>
          <div style={{ fontSize: 14, color: 'var(--text2)' }}>{t('topupNoRules')}</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{t('topupNoRulesHint')}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {bonuses.map(b => {
            const pct = b.min_amount > 0 ? ((b.bonus_amount / b.min_amount) * 100).toFixed(1) : 0;
            return (
              <div key={b.id} style={{
                background: 'var(--card2)', border: `1px solid ${b.is_active ? 'var(--border)' : 'rgba(255,59,92,0.3)'}`,
                borderRadius: 12, padding: 16, opacity: b.is_active ? 1 : 0.6,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>{t('topupRuleHash')}{b.id}</span>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 700,
                    background: b.is_active ? 'rgba(0,255,136,0.12)' : 'rgba(255,59,92,0.1)',
                    color: b.is_active ? 'var(--green)' : 'var(--red)' }}>
                    {b.is_active ? t('topupActive') : t('topupInactive')}
                  </span>
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 800, color: 'var(--cyan)' }}>
                  {Number(b.min_amount).toLocaleString()} {t('currency')}
                </div>
                <div style={{ color: 'var(--text3)', fontSize: 11, margin: '4px 0' }}>{t('topupWhenDeposited')}</div>
                <div style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: 'var(--green)' }}>
                  + {Number(b.bonus_amount).toLocaleString()} {t('currency')} 🎁
                </div>
                <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 3 }}>~{pct}% bonus</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 14 }}>
                  <button onClick={() => edit(b)} style={{
                    flex: 1, background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)',
                    color: 'var(--cyan)', padding: '6px 10px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>{t('topupEditBtn')}</button>
                  <button onClick={() => toggleActive(b)} style={{
                    background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
                    padding: '6px 10px', borderRadius: 7, fontSize: 11, cursor: 'pointer',
                  }}>{b.is_active ? '⏸' : '▶'}</button>
                  <button onClick={() => remove(b)} style={{
                    background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)', color: 'var(--red)',
                    padding: '6px 10px', borderRadius: 7, fontSize: 11, cursor: 'pointer',
                  }}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
